/**
 * Offline Banner Component
 * Sprint 31.7: Updated to use CSS classes from index.css (no Tailwind).
 *
 * Displays a persistent banner when network connectivity is lost.
 * Shows reconnection attempts and allows manual retry.
 */
import { useNetworkStatus, formatOfflineDuration } from '../../hooks/useNetworkStatus';
import { useState } from 'react';

export function OfflineBanner() {
  const { isOnline, isApiReachable, offlineDuration, checkConnectivity } = useNetworkStatus();
  const [isRetrying, setIsRetrying] = useState(false);

  // Don't show banner if fully connected
  if (isOnline && isApiReachable) {
    return null;
  }

  const handleRetry = async () => {
    setIsRetrying(true);
    await checkConnectivity();
    setIsRetrying(false);
  };

  // Determine message based on state
  const message = !isOnline
    ? 'You are offline. Changes will sync when connection is restored.'
    : 'Unable to reach the server. Some features may be unavailable.';

  const icon = !isOnline ? (
    // Offline icon (no wifi)
    <svg className="offline-banner-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a5 5 0 01-7.072-7.072m7.072 7.072l2.829-2.829M3 3l3.757 3.757m0 0a9 9 0 0110.986 1.106" />
    </svg>
  ) : (
    // Server unreachable icon
    <svg className="offline-banner-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );

  return (
    <div
      className="offline-banner"
      role="alert"
      aria-live="polite"
    >
      {icon}
      
      <div className="offline-banner-content">
        <p className="offline-banner-message">{message}</p>
        {offlineDuration > 0 && (
          <p className="offline-banner-duration">
            Offline for {formatOfflineDuration(offlineDuration)}
          </p>
        )}
      </div>

      <button
        onClick={handleRetry}
        disabled={isRetrying}
        className="offline-banner-retry"
      >
        {isRetrying ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <svg className="loading-spinner sm" fill="none" viewBox="0 0 24 24">
              <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Retrying...
          </span>
        ) : (
          'Retry'
        )}
      </button>
    </div>
  );
}
